import React, { useState, useEffect } from "react";
import classnames from "classnames/bind";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

import { getFireDB, getRoomImages } from "src/utils/Firebase";
import css from "./index.scss";

const cx = classnames.bind(css);
const moduleName = "RoomDetail";

const FOLDERS = ['a', 'b', 'c', 'd', 'e', 'f'];

const AIRBNB_URLS = {
  a: 'https://www.airbnb.co.kr/rooms/608402008455145466',
  b: 'https://www.airbnb.co.kr/rooms/611397816227201990',
  c: 'https://www.airbnb.co.kr/rooms/794560958286116409',
  d: 'https://www.airbnb.co.kr/rooms/864919663376885581',
  e: 'https://www.airbnb.co.kr/rooms/896846586854616815',
  f: 'https://www.airbnb.co.kr/rooms/920709827813971168',
};

const HIGHLIGHTS = [
  { icon: "🌲", title: "우거진 숲 속", desc: "사방이 자연으로 둘러싸인 독채 공간" },
  { icon: "🌊", title: "강이 흐르는 곳", desc: "도보 거리에 맑은 강과 천렵 포인트" },
  { icon: "🔥", title: "바베큐 & 야외공간", desc: "전용 바베큐 그릴과 넓은 마당" },
];

const RoomDetail = ({ match, location }) => {
  const { params: { id } = {} } = match;
  const roomId = Number(id);

  const folderFromState = location && location.state && location.state.folder;
  const [folder, setFolder] = useState(folderFromState || null);
  const [room, setRoom]     = useState(null);
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState(null); // 클릭한 이미지 index

  useEffect(() => {
    getFireDB().then((res) => {
      const rooms = res.val().rooms;
      if (!rooms) return;
      const list = Object.values(rooms).sort((a, b) => a.id - b.id);
      const found = list.find((r) => r.id === roomId);
      if (found) setRoom(found);
      if (!folderFromState) {
        const idx = list.findIndex((r) => r.id === roomId);
        setFolder(FOLDERS[idx] || 'a');
      }
    });
  }, [roomId]);

  useEffect(() => {
    if (!folder) return;
    getRoomImages(folder).then((urls) => setImages(urls));
  }, [folder]);

  // 라이트박스 키보드 탐색
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLightbox((i) => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft')  setLightbox((i) => Math.max(i - 1, 0));
      if (e.key === 'Escape')     setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, images.length]);

  if (!room) return null;

  return (
    <div className={cx(moduleName)}>

      {/* ─── 메인 히어로 이미지 ─── */}
      {images[0] && (
        <button
          className={cx(`${moduleName}-hero`)}
          onClick={() => setLightbox(0)}
        >
          <img src={images[0]} alt={room.title} className={cx(`${moduleName}-hero-img`)} />
        </button>
      )}

      {/* ─── 나머지 사진 갤러리 ─── */}
      {images.length > 1 && (
        <div className={cx(`${moduleName}-gallery`)}>
          <div className={cx(`${moduleName}-gallery-header`)}>
            <span className={cx(`${moduleName}-gallery-count`)}>{images.length}장의 사진</span>
          </div>
          <div className={cx(`${moduleName}-gallery-grid`)}>
            {images.slice(1).map((url, i) => (
              <button key={i} className={cx(`${moduleName}-gallery-item`)} onClick={() => setLightbox(i + 1)}>
                <img src={url} alt={`${room.title} ${i + 2}`} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── 라이트박스 ─── */}
      {lightbox !== null && (
        <div className={cx(`${moduleName}-lightbox`)} onClick={() => setLightbox(null)}>
          <button
            className={cx(`${moduleName}-lightbox-close`)}
            onClick={() => setLightbox(null)}
          >✕</button>
          <button
            className={cx(`${moduleName}-lightbox-prev`)}
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.max(i - 1, 0)); }}
            disabled={lightbox === 0}
          >‹</button>
          <img
            src={images[lightbox]}
            alt={room.title}
            className={cx(`${moduleName}-lightbox-img`)}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className={cx(`${moduleName}-lightbox-next`)}
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => Math.min(i + 1, images.length - 1)); }}
            disabled={lightbox === images.length - 1}
          >›</button>
          <p className={cx(`${moduleName}-lightbox-num`)}>{lightbox + 1} / {images.length}</p>
        </div>
      )}

      {/* ─── 콘텐츠 ─── */}
      <div className={cx(`${moduleName}-body`)}>

        {/* 제목 섹션 */}
        <div className={cx(`${moduleName}-header`)}>
          <div className={cx(`${moduleName}-header-left`)}>
            <p className={cx(`${moduleName}-header-label`)}>강원도 영월군 무릉도원면</p>
            <h1 className={cx(`${moduleName}-header-title`)}>{room.title}</h1>
            {room.size && (
              <p className={cx(`${moduleName}-header-size`)}>{room.size}평형</p>
            )}
          </div>
          <Link to="/room" className={cx(`${moduleName}-back-link`)}>← 목록</Link>
        </div>

        <div className={cx(`${moduleName}-divider`)} />

        {/* 하이라이트 */}
        <div className={cx(`${moduleName}-highlights`)}>
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className={cx(`${moduleName}-highlight`)}>
              <span className={cx(`${moduleName}-highlight-icon`)}>{h.icon}</span>
              <div>
                <p className={cx(`${moduleName}-highlight-title`)}>{h.title}</p>
                <p className={cx(`${moduleName}-highlight-desc`)}>{h.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={cx(`${moduleName}-divider`)} />

        {/* 공간 설명 */}
        <div className={cx(`${moduleName}-about`)}>
          <h3 className={cx(`${moduleName}-about-title`)}>공간 소개</h3>
          <p className={cx(`${moduleName}-about-text`)}>{room.content}</p>
        </div>

        {/* 시설 안내 */}
        {room.description && (
          <React.Fragment>
            <div className={cx(`${moduleName}-divider`)} />
            <div className={cx(`${moduleName}-facilities`)}>
              <h3 className={cx(`${moduleName}-facilities-title`)}>시설 안내</h3>
              <p className={cx(`${moduleName}-facilities-text`)}>{room.description}</p>
            </div>
          </React.Fragment>
        )}

        <div className={cx(`${moduleName}-divider`)} />

        {/* Airbnb 예약 */}
        {folder && AIRBNB_URLS[folder] && (
          <a
            href={AIRBNB_URLS[folder]}
            target="_blank"
            rel="noopener noreferrer"
            className={cx(`${moduleName}-airbnb-btn`)}
          >
            Airbnb에서 예약하기
          </a>
        )}

        <div className={cx(`${moduleName}-divider`)} />

        {/* 예약 문의 */}
        <div className={cx(`${moduleName}-cta`)}>
          <p className={cx(`${moduleName}-cta-label`)}>예약 및 문의</p>
          <a href="tel:01047386659" className={cx(`${moduleName}-cta-tel`)}>
            010 · 4738 · 6659
          </a>
        </div>
      </div>
    </div>
  );
};

export default connect(null)(RoomDetail);



// WEBPACK FOOTER //
// ./src/App/pages/Home/pages/RoomDetail/index.js